// Feature #313 — the "Capability badges" block on My Skills and Staff Detail.
//
// Originally the right-hand half of the core competency radar block, filling
// the space Feature #311 vacated when it retired the self/peer comparison
// table. Feature #355 retired the radar itself (core competencies are no
// longer part of Skills Management), so this is now a block of its own,
// with its own heading, rendered above the technical strength spine.
//
// Feature #356 — a badge is assigned by a Skills Manager, not earned from
// ratings. This block is where a manager does that: with `canManage` it
// gains an "Assign capability…" control and a remove control on each chip.
// It is the one component that offers those controls — Staff Detail and the
// Capabilities page both render it — so the two surfaces cannot drift.
//
// Shows **held badges only** to a viewer who cannot manage. That is what a
// badge means, and it is also the disclosure line: a badge states a
// positive. The manager's assign picker is the only place an unheld
// capability is named here, and only a manager sees it.
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { assignCapability, revokeCapability, SmCapabilityBadge } from '@/api/sm'
import { formatDate } from '@/utils/dateFormat'
import { blockStyle } from './SmShared'

interface CapabilityBadgesProps {
  capabilities: SmCapabilityBadge[]
  /** Written into the empty state so Staff Detail doesn't say "you". */
  subjectLabel: string
  /** Whose badges these are. Required when `canManage` is set. */
  subjectUserId?: string
  /**
   * Show the assign and remove controls. The caller decides: sm_manager or
   * platform_admin, and NOT looking at themselves — the API refuses a
   * self-assignment with 403 regardless, so this is convenience, not the
   * control.
   */
  canManage?: boolean
  /** Called after a successful assign or remove so the caller can reload. */
  onChanged?: () => void
}

const badgeChip: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 12px',
  borderRadius: '999px',
  backgroundColor: '#f3e8f1',
  color: 'var(--color-dark-magenta)',
  border: '1px solid var(--color-magenta-tint)',
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: 1.3,
}

const removeButton: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--color-dark-magenta)',
  cursor: 'pointer',
  fontSize: '15px',
  lineHeight: 1,
  padding: '0 0 0 2px',
  minHeight: 'auto',
  fontFamily: 'var(--font-family)',
}

const assignButton: React.CSSProperties = {
  padding: '6px 14px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  minHeight: 'auto',
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

/** "Assigned by Mel Manager on 18 Sep 2026 — note" for the chip's title. */
export const badgeTitle = (badge: SmCapabilityBadge): string | undefined => {
  if (!badge.met) return badge.description || undefined
  const by = badge.assigned_by_label || 'a Skills Manager'
  const when = badge.assigned_at ? ` on ${formatDate(badge.assigned_at)}` : ''
  const note = badge.note ? ` — ${badge.note}` : ''
  return `Assigned by ${by}${when}${note}`
}

export const CapabilityBadges: React.FC<CapabilityBadgesProps> = ({
  capabilities,
  subjectLabel,
  subjectUserId,
  canManage = false,
  onChanged,
}) => {
  const held = useMemo(() => capabilities.filter((c) => c.met), [capabilities])
  // Only ever consulted when `canManage`; the picker is not rendered
  // otherwise, so a non-manager is never shown what the subject lacks.
  const assignable = useMemo(() => capabilities.filter((c) => !c.met), [capabilities])

  const [pick, setPick] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const manage = canManage && !!subjectUserId

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      onChanged?.()
    } catch (e) {
      // The 409 on an already-held badge and the 403 on naming yourself
      // both arrive as `detail`; losing them to a generic message would
      // make those states unexplainable.
      const err = e as { response?: { data?: { detail?: string } }; message?: string }
      setError(err?.response?.data?.detail || err?.message || 'Action failed')
    } finally {
      setBusy(false)
    }
  }

  const handleAssign = () => {
    if (!pick || !subjectUserId) return
    run(async () => {
      await assignCapability(pick, {
        user_id: subjectUserId,
        note: note.trim() || undefined,
      })
      setPick('')
      setNote('')
    })
  }

  const handleRemove = (badge: SmCapabilityBadge) => {
    if (!subjectUserId) return
    if (
      !window.confirm(
        `Remove the ${badge.name} badge from ${subjectLabel}? ` +
          'The assignment is kept as history and can be given again later.'
      )
    )
      return
    run(() => revokeCapability(badge.capability_id, subjectUserId))
  }

  return (
    <section style={blockStyle} aria-labelledby="sm-capability-badges-heading">
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '10px',
          marginBottom: '6px',
        }}
      >
        <h2
          id="sm-capability-badges-heading"
          style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}
        >
          Capability badges
        </h2>
        {held.length > 0 && (
          <span
            style={{
              fontSize: '13px',
              color: 'var(--color-dark-grey)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {held.length}
          </span>
        )}
      </div>

      {held.length === 0 ? (
        <p style={{ color: 'var(--color-dark-grey)', fontSize: '13px', margin: '4px 0 0' }}>
          No capability badges yet.
          {!manage && (
            <>
              {' '}
              A badge is assigned by a Skills Manager.{' '}
              <Link to="/sm/capabilities">View all capabilities</Link>.
            </>
          )}
        </p>
      ) : (
        <>
          <p
            style={{
              color: 'var(--color-dark-grey)',
              fontSize: '13px',
              margin: '0 0 12px',
            }}
          >
            Capabilities {subjectLabel === 'You' ? 'you have' : `${subjectLabel} has`} been
            assigned. Hover a badge to see who assigned it and when.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {held.map((c) => (
              <span key={c.capability_id} style={badgeChip} title={badgeTitle(c)}>
                {c.name}
                {manage && (
                  <button
                    type="button"
                    onClick={() => handleRemove(c)}
                    disabled={busy}
                    aria-label={`Remove ${c.name} from ${subjectLabel}`}
                    title={`Remove ${c.name}`}
                    style={{ ...removeButton, cursor: busy ? 'wait' : 'pointer' }}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
          {!manage && (
            <p style={{ margin: '14px 0 0', fontSize: '13px' }}>
              <Link to="/sm/capabilities">View all capabilities</Link>
            </p>
          )}
        </>
      )}

      {manage && (
        <div
          style={{
            marginTop: '14px',
            paddingTop: '14px',
            borderTop: '1px dashed var(--color-light-grey)',
            display: 'grid',
            gap: '8px',
          }}
        >
          <div
            style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}
          >
            <select
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              disabled={busy || assignable.length === 0}
              aria-label={`Assign a capability to ${subjectLabel}`}
              style={{ flex: '1 1 220px', minHeight: '34px', fontSize: '13px' }}
            >
              <option value="">
                {assignable.length === 0
                  ? 'Every capability is already assigned'
                  : 'Assign capability…'}
              </option>
              {assignable.map((c) => (
                <option key={c.capability_id} value={c.capability_id}>
                  {c.name}
                </option>
              ))}
            </select>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note (optional)"
              maxLength={1000}
              disabled={busy}
              aria-label="Note for this assignment"
              style={{ flex: '2 1 260px', fontSize: '13px', minHeight: '34px' }}
            />
            <button
              type="button"
              onClick={handleAssign}
              disabled={busy || !pick}
              style={{
                ...assignButton,
                cursor: busy ? 'wait' : !pick ? 'not-allowed' : 'pointer',
                opacity: !pick ? 0.6 : 1,
              }}
            >
              {busy ? 'Working…' : 'Assign'}
            </button>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--color-dark-grey)', margin: 0 }}>
            A badge is your statement that {subjectLabel} can deliver this. It shows on
            their profile immediately, with your name and today&apos;s date.
          </p>
          {error && (
            <div role="alert" style={{ color: '#b91c1c', fontSize: '13px' }}>
              {error}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

export default CapabilityBadges
